<template>
  <div class="notification-center">
    <v-dialog
      v-if="media.isMobile"
      v-model="openPopup"
      :close-on-content-click="false"
      :min-width="320"
      content-class="ms-5"
      offset-y
      top
    >
      <template #activator="{ on, attrs }">
        <div class="px-0" style="position: relative" v-bind="attrs" v-on="on">
          <v-badge
            bordered
            :color="isNotDisturb ? 'grey' : 'primary'"
            :content="normalizedUnreadCount"
            :value="unreadCount"
            overlap
          >
            <div v-bind="attrs" v-on="on">
              <v-icon v-if="isNotDisturb">mdi-bell-off</v-icon>
              <v-icon v-else color="primary">mdi-bell</v-icon>
            </div>
          </v-badge>
        </div>
      </template>

      <notification-center-card
        :is-loading="isLoading"
        :is-not-disturb="isNotDisturb"
        @read-all="readAll"
        @toggle-notified="toggleNotified"
        @close-popup="openPopup = false"
      >
        <notification-center-card-item
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          :issue-id="notification.data.issueId"
          class="mb-4"
      /></notification-center-card>
    </v-dialog>
    <v-menu
      v-else
      v-model="openPopup"
      :close-on-content-click="false"
      :min-width="500"
      content-class="ms-5"
      offset-x
      top
    >
      <template #activator="{ on: onMenu }">
        <v-tooltip right content-class="layout-sidebar__tooltip">
          <template #activator="{ on: onTooltip }">
            <v-btn
              v-if="isNotDisturb"
              class="layout-sidebar__item px-0"
              icon
              width="60px"
              v-on="{ ...onMenu, ...onTooltip }"
            >
              <v-icon>mdi-bell-off</v-icon>
              <v-avatar
                v-if="unreadCount"
                size="20"
                class="layout-sidebar__badge ml-2 d-flex align-center justify-center"
                color="secondary"
              >
                <span class="primary--text text-subtitle-2">
                  {{ normalizedUnreadCount }}
                </span>
              </v-avatar>
            </v-btn>
            <v-btn v-else class="layout-sidebar__item px-0" icon width="60px" v-on="{ ...onMenu, ...onTooltip }">
              <v-icon>mdi-bell</v-icon>
              <v-avatar
                v-if="unreadCount"
                size="20"
                class="layout-sidebar__badge ml-2 d-flex align-center justify-center"
                color="secondary"
              >
                <span class="primary--text text-subtitle-2">
                  {{ normalizedUnreadCount }}
                </span>
              </v-avatar>
            </v-btn>
          </template>
          <span>{{ $t('sidebar.notifications') }}</span>
        </v-tooltip>
      </template>

      <notification-center-card
        :is-loading="isLoading"
        :is-not-disturb="isNotDisturb"
        @read-all="readAll"
        @toggle-notified="toggleNotified"
      >
        <notification-center-card-item
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          :issue-id="notification.data.issueId"
          class="mb-4"
      /></notification-center-card>
    </v-menu>
  </div>
</template>

<script>
import NotificationCenterCard from '@/components/NotificationCenterCard.vue';
import NotificationCenterCardItem from '@/components/NotificationCenterCardItem.vue';

import notificationsCenter from '@/services/notificationsCenter';
import notificationService from '@/services/notification';
import { loadStorageItem, saveStorageItem } from '@/services/storage';

import { mapGetters, mapActions } from 'vuex';

import { UNREAD_COUNT, NOTIFICATION_LIST, IS_NOT_DISTURB } from '@/store/modules/notifications/types';
import {
  SET_NOTIFICATION_LIST,
  SET_UNREAD_COUNT_NOTIFICATIONS,
  ADD_NEW_NOTIFICATION,
  SET_DISTURB,
} from '@/store/modules/notifications/actions/types';
import { TASKS_DETAILED } from '@/constants/routes';

const PUSH_TIMEOUT = 10000;

export default {
  components: { NotificationCenterCard, NotificationCenterCardItem },

  inject: ['media'],
  data: () => ({
    openPopup: false,
    isLoading: false,
    notificationsPush: [],
    timeoutMainNotification: {},
  }),

  computed: {
    ...mapGetters('notifications', {
      unreadCount: UNREAD_COUNT,
      notifications: NOTIFICATION_LIST,
      isNotDisturb: IS_NOT_DISTURB,
    }),

    normalizedUnreadCount() {
      return this.unreadCount > 9 ? '9+' : this.unreadCount;
    },

    activePushNotificationsCount() {
      return this.notificationsPush.length;
    },
  },

  watch: {
    activePushNotificationsCount(count) {
      if (count > 3) {
        this.showMainNotification();
      }
    },
  },

  beforeMount() {
    this.unsubscribe = this.$store.subscribe(mutation => {
      if (mutation.type === ADD_NEW_NOTIFICATION || mutation.type === `notifications/${ADD_NEW_NOTIFICATION}`) {
        this.showNewNotification(mutation.payload);
      }
    });
  },

  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },

  mounted() {
    this.isLoading = true;
    this.setDisturb(loadStorageItem('isNotDisturb') === 'true');

    notificationService.addEventListener('open-notification-center', () => {
      this.openPopup = true;
    });

    notificationService.addEventListener('removed', item => {
      if (this.$options.mainNotification && this.$options.mainNotification.id === item.id) {
        this.$options.mainNotification = null;
      }
    });

    this.getNotifications().then(({ unreadCount }) => {
      if (unreadCount && !this.isNotDisturb) {
        this.$options.mainNotification = notificationService.info(
          `${this.$t('notifications.new_notifications', { count: unreadCount })}`,
          {
            timeout: PUSH_TIMEOUT,
            btnNotificationCenter: true,
          }
        );

        if (this.timeoutMainNotification) {
          clearTimeout(this.timeoutMainNotification);
        }

        this.timeoutMainNotification = setTimeout(() => {
          this.$options.mainNotification = null;
          this.timeoutMainNotification = null;
        }, PUSH_TIMEOUT);
      }
    });
  },

  methods: {
    ...mapActions('notifications', {
      setNotificationsList: SET_NOTIFICATION_LIST,
      setUnreadCount: SET_UNREAD_COUNT_NOTIFICATIONS,
      setDisturb: SET_DISTURB,
    }),

    async getNotifications() {
      return notificationsCenter
        .getNotifications()
        .then(({ results, unreadCount }) => {
          this.setNotificationsList(results);
          this.setUnreadCount(unreadCount);
          return { results, unreadCount };
        })
        .catch(() => ({}))
        .finally(() => {
          this.isLoading = false;
        });
    },

    readAll() {
      notificationsCenter
        .readAllNotifications()
        .then(({ results, unreadCount }) => {
          this.setNotificationsList(results);
          this.setUnreadCount(unreadCount);
        })
        .catch(() => {});
    },

    toggleNotified() {
      this.setDisturb(!this.isNotDisturb);
      saveStorageItem('isNotDisturb', this.isNotDisturb);
    },

    showNewNotification(notification) {
      if (this.isNotDisturb) {
        return;
      }
      if (this.$options.mainNotification) {
        this.$nextTick(() => {
          this.showMainNotification();
        });
      } else {
        this.notificationsPush.push(
          notificationService.info(notification.message, {
            timeout: PUSH_TIMEOUT,
            linkTo: { name: TASKS_DETAILED, params: { id: notification.data.issueId, prevPage: this.$route } },
          })
        );

        setTimeout(() => {
          this.notificationsPush = this.notificationsPush.filter(item => item.id !== notification.id);
        }, PUSH_TIMEOUT);
      }
    },

    showMainNotification() {
      if (this.isNotDisturb) {
        return;
      }
      this.notificationsPush.forEach(notification => {
        notificationService.remove(notification);
      });

      if (this.$options.mainNotification) {
        this.$options.mainNotification = notificationService.updateMessage(
          this.$options.mainNotification,
          `${this.$t('notifications.new_notifications', { count: this.unreadCount })}`
        );
      } else {
        this.$options.mainNotification = notificationService.info(
          `${this.$t('notifications.new_notifications', { count: this.unreadCount })}`,
          {
            timeout: PUSH_TIMEOUT,
            btnNotificationCenter: true,
          }
        );
      }

      if (this.timeoutMainNotification) {
        clearTimeout(this.timeoutMainNotification);
      }

      this.timeoutMainNotification = setTimeout(() => {
        this.$options.mainNotification = null;
        this.timeoutMainNotification = null;
      }, PUSH_TIMEOUT);
    },
  },

  mainNotification: null,
};
</script>
